import {
  SelectProps,
  Select,
  SelectItem,
  SelectSection,
  SelectSectionProps,
  Checkbox,
  Skeleton,
} from "@heroui/react";
import { MantineFormValues } from "./shared.types";
import { t } from "@lingui/core/macro";
import { useMemo, useCallback, useState } from "react";
import { Check, List, ListChecks, Plus, Search } from "lucide-react";
import { cn } from "@/utils";
import { LimeInput } from "./LimeInput";
import {
  SELECT_ALL_ITEM_KEY,
  resolveMultiSelectChange,
} from "./multiSelectChange";
import { filterSelectItems } from "./selectSearch";

export type CreateNewOption = {
  label: string;
  sectionName?: string;
  icon?: React.ReactNode;
  customLabel?: React.ReactNode;
  onCreateNew: () => void;
};

export type LimeSelectProps = Omit<
  MantineFormValues & SelectProps,
  "children"
> & {
  items: {
    label: string;
    customLabel?: React.ReactNode;
    key: string;
    identifier?: string;
    sectionName?: string;
    startContent?: React.ReactNode;
    endContent?: React.ReactNode;
  }[];
  onSingleItemChange?: (value: string) => void;
  onMultipleItemChange?: (value: string[]) => void;
  emptyContentMessage?: string;
  sectionProps?: Omit<SelectSectionProps, "children">;
  showSelectAllToggle?: boolean;
  createNewOption?: CreateNewOption;
  showSectionSelectAllToggle?: boolean;
  isSkeleton?: boolean;
  isSearchable?: boolean;
  searchPlaceholder?: string;
};

/**
 * change handlers ( omit default hanlder )
 * - when using single mode : onSingleItemChange
 * - when using multiple mode : onMultipleItemChange
 *
 * for value:
 * - use value prop instead of selectedKeys
 */

export const LimeSelect = ({
  emptyContentMessage = t`Ni rezultatov`,
  error,
  value,
  showSelectAllToggle = false,
  createNewOption,
  showSectionSelectAllToggle = false,
  isSkeleton = false,
  isSearchable = false,
  searchPlaceholder,
  ...props
}: LimeSelectProps) => {
  const [searchQuery, setSearchQuery] = useState("");

  const allItems = useMemo(() => {
    if (!createNewOption) return props.items;

    const createNewItem: LimeSelectProps["items"][number] = {
      key: "__CREATE_NEW__",
      label: createNewOption.label,
      customLabel: createNewOption.customLabel,
      sectionName: createNewOption.sectionName,
      startContent: createNewOption.icon ?? <Plus size={16} />,
      endContent: undefined,
    };

    return [createNewItem, ...props.items];
  }, [props.items, createNewOption]);

  const visibleItems = useMemo(() => {
    if (!isSearchable) return allItems;
    return filterSelectItems(allItems, searchQuery, ["__CREATE_NEW__"]);
  }, [allItems, isSearchable, searchQuery]);

  const itemsBySection = useMemo(() => {
    return visibleItems.reduce(
      (acc, item) => {
        const section = item.sectionName || "default";
        if (!acc[section]) {
          acc[section] = [];
        }
        acc[section].push(item);
        return acc;
      },
      {} as Record<string, typeof visibleItems>,
    );
  }, [visibleItems]);

  const allItemKeys = useMemo(() => {
    return visibleItems.flatMap((item) =>
      item.key !== "__CREATE_NEW__" &&
      (props.disabledKeys
        ? !Object.keys(props.disabledKeys).includes(item.key)
        : true)
        ? [item.key]
        : [],
    );
  }, [visibleItems]);

  const selectedKeys = useMemo(() => {
    if (!value) return [];
    return Array.isArray(value)
      ? (value as (string | number)[]).map((v) => v.toString())
      : [value.toString()];
  }, [value]);

  const currentSelectedKeys = useMemo(() => {
    if (props.selectionMode !== "multiple") return [];
    return Array.isArray(value) ? selectedKeys : [];
  }, [value, selectedKeys, props.selectionMode]);

  const areAllItemsSelected = useMemo(() => {
    return (
      allItemKeys.length > 0 &&
      allItemKeys.every((key) => currentSelectedKeys.includes(key))
    );
  }, [allItemKeys, currentSelectedKeys]);

  // Toggling adds to / subtracts from the current selection rather than
  // replacing it, so that with an active search "select all" means "all
  // matches" without silently dropping what is selected outside the search.
  const handleSelectAllToggle = useCallback(() => {
    if (props.selectionMode === "multiple") {
      if (areAllItemsSelected) {
        const remaining = currentSelectedKeys.filter(
          (key) => !allItemKeys.includes(key),
        );

        if (
          remaining.length === 0 &&
          (props.disallowEmptySelection || props.disallowEmptySelection == null)
        ) {
          props.onMultipleItemChange?.(
            props.items[0] != null ? [props.items[0].key] : [],
          );
        } else {
          props.onMultipleItemChange?.(remaining);
        }
      } else {
        const merged = [
          ...new Set([...currentSelectedKeys, ...allItemKeys]),
        ].filter((s) => !s.startsWith("__select_all"));

        props.onMultipleItemChange?.(merged);
      }
    }
  }, [
    props.selectionMode,
    areAllItemsSelected,
    props.disallowEmptySelection,
    props.onMultipleItemChange,
    props.items,
    currentSelectedKeys,
    allItemKeys,
  ]);

  const handleSectionSelectAllToggle = useCallback(
    (sectionKeys: string[]) => {
      if (props.selectionMode !== "multiple") return;

      const allSectionSelected = sectionKeys.every((key) =>
        currentSelectedKeys.includes(key),
      );

      if (allSectionSelected) {
        const remaining = currentSelectedKeys.filter(
          (key) => !sectionKeys.includes(key),
        );

        if (
          remaining.length === 0 &&
          (props.disallowEmptySelection || props.disallowEmptySelection == null)
        ) {
          props.onMultipleItemChange?.(
            props.items[0] != null ? [props.items[0].key] : [],
          );
        } else {
          props.onMultipleItemChange?.(remaining);
        }
      } else {
        const merged = [
          ...new Set([...currentSelectedKeys, ...sectionKeys]),
        ].filter((s) => !s.startsWith("__select_all"));

        props.onMultipleItemChange?.(merged);
      }
    },
    [
      props.selectionMode,
      currentSelectedKeys,
      props.disallowEmptySelection,
      props.onMultipleItemChange,
      props.items,
    ],
  );

  const renderedItems = useMemo(() => {
    const items = Object.keys(itemsBySection)
      .map((sectionName) => {
        const items = itemsBySection[sectionName];

        if (sectionName === "default") {
          return items.map((item) => (
            <SelectItem
              key={item.key}
              data-identifier={item.identifier}
              startContent={item.startContent}
              endContent={item.endContent}
              aria-label={
                typeof item.label === "string" ? item.label : undefined
              }
            >
              {item.customLabel ?? item.label}
            </SelectItem>
          ));
        }

        const sectionKeys = items.map((item) => item.key);
        const allSectionSelected =
          showSectionSelectAllToggle &&
          props.selectionMode === "multiple" &&
          sectionKeys.length > 0 &&
          sectionKeys.every((key) => currentSelectedKeys.includes(key));

        const sectionTitle =
          showSectionSelectAllToggle && props.selectionMode === "multiple" ? (
            <Checkbox
              isSelected={allSectionSelected}
              onValueChange={() => handleSectionSelectAllToggle(sectionKeys)}
              size="md"
              classNames={{
                base: "w-full max-w-full m-0 p-0",
                wrapper: "ml-0 mr-3",
                label: "text-xs text-gray",
              }}
            >
              {sectionName}
            </Checkbox>
          ) : (
            sectionName
          );

        return (
          <SelectSection
            key={sectionName}
            // @ts-expect-error HeroUI types narrow title to string (from HTML attrs), but the component accepts ReactNode
            title={sectionTitle}
            showDivider
            {...props.sectionProps}
            classNames={{
              heading:
                "flex w-full sticky top-1 z-20 py-1.5 px-2 bg-default-100 shadow-small rounded-small",
              ...props.sectionProps?.classNames,
            }}
          >
            {items.map((item) => (
              <SelectItem
                key={item.key}
                data-identifier={item.identifier}
                startContent={item.startContent}
                endContent={item.endContent}
                aria-label={
                  typeof item.label === "string" ? item.label : undefined
                }
              >
                {item.customLabel ?? item.label}
              </SelectItem>
            ))}
          </SelectSection>
        );
      })
      .flat();

    // A selected key that the search filtered out must stay in the collection,
    // otherwise HeroUI drops it from the trigger's rendered value and warns
    // about keys missing from the collection on every keystroke.
    if (visibleItems.length > 0 && visibleItems.length < allItems.length) {
      const hiddenSelectedItems = allItems.flatMap((item) =>
        selectedKeys.includes(item.key) &&
        !visibleItems.some((visible) => visible.key === item.key)
          ? [
              <SelectItem
                key={item.key}
                className="hidden"
                aria-label={
                  typeof item.label === "string" ? item.label : undefined
                }
              >
                {item.customLabel ?? item.label}
              </SelectItem>,
            ]
          : [],
      );

      items.push(...hiddenSelectedItems);
    }

    // Add select all toggle at the beginning if enabled and in multiple mode
    if (
      showSelectAllToggle &&
      props.selectionMode === "multiple" &&
      allItemKeys.length > 0
    ) {
      const selectAllItem = (
        <SelectItem
          key={SELECT_ALL_ITEM_KEY}
          aria-label={
            areAllItemsSelected ? t`Prekliči izbiro vseh` : t`Izberi vse`
          }
          onPress={handleSelectAllToggle}
          startContent={
            !areAllItemsSelected ? <ListChecks size={16} /> : <List size={16} />
          }
          endContent={areAllItemsSelected ? <Check size={14} /> : undefined}
        >
          {areAllItemsSelected ? t`Prekliči izbiro vseh` : t`Izberi vse`}
        </SelectItem>
      );

      return [selectAllItem, ...items];
    }

    return items;
  }, [
    itemsBySection,
    props.sectionProps,
    showSelectAllToggle,
    showSectionSelectAllToggle,
    props.selectionMode,
    areAllItemsSelected,
    handleSelectAllToggle,
    handleSectionSelectAllToggle,
    currentSelectedKeys,
    allItemKeys,
    allItems,
    visibleItems,
    selectedKeys,
  ]);

  return (
    <Skeleton
      isLoaded={!isSkeleton}
      className={cn(
        props.className,
        "w-full rounded-xl before:pointer-events-none",
      )}
    >
      <Select
        errorMessage={error}
        isInvalid={!!error}
        selectedKeys={
          value
            ? props.selectionMode === "multiple"
              ? value.map((v: number | string) => v.toString())
              : [value.toString()]
            : []
        }
        disallowEmptySelection
        selectionMode={props.selectionMode || "single"}
        popoverProps={{
          shouldBlockScroll: false,
          shouldCloseOnScroll: false,
          ...(props.popoverProps || {}),
        }}
        aria-label={typeof props.label === "string" ? props.label : "select"}
        {...props}
        isDisabled={props.isDisabled || props.isLoading}
        isVirtualized={props.isVirtualized ?? false}
        onOpenChange={(isOpen) => {
          if (!isOpen) setSearchQuery("");
          props.onOpenChange?.(isOpen);
        }}
        listboxProps={{
          ...(props.listboxProps || {}),
          emptyContent: emptyContentMessage,
          disableAnimation: true,
          itemClasses: props.listboxProps?.itemClasses,
          topContent: isSearchable ? (
            <div className="bg-content1 sticky top-0 z-30 px-1 pt-1 pb-2">
              <LimeInput
                size="sm"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={searchPlaceholder ?? t`Išči`}
                startContent={
                  <Search size={16} className="text-default-400 shrink-0" />
                }
                isClearable
                onClear={() => setSearchQuery("")}
                aria-label={searchPlaceholder ?? t`Išči`}
              />
            </div>
          ) : (
            props.listboxProps?.topContent
          ),
        }}
        onChange={(e) => {
          // Handle create new option
          if (e.target.value === "__CREATE_NEW__") {
            createNewOption?.onCreateNew();
            return;
          }

          if (props.selectionMode === "multiple") {
            // Pressing the select-all row is owned by handleSelectAllToggle
            // (onPress); the change event HeroUI fires for it must be ignored
            // here, otherwise it overwrites the toggle result with the
            // pre-press keys.
            const change = resolveMultiSelectChange(e.target.value);
            if (change.type === "apply") {
              props.onMultipleItemChange?.(change.keys);
            }
            return;
          } else {
            props.onSingleItemChange?.(e.target.value);
          }

          props.onChange?.(e.target.value);
        }}
      >
        {renderedItems}
      </Select>
    </Skeleton>
  );
};
